"use client";

/**
 * global-error — the last-resort boundary for uncaught errors thrown by the
 * root layout itself. Next.js renders this in place of the entire document, so
 * it MUST provide its own <html> and <body>. The styling is deliberately
 * inline + self-contained: at this point the app's CSS may not have loaded.
 *
 * Kept calm and minimal for a non-technical audience. No stack dump.
 */

import { useEffect } from "react";
import { reportError } from "@/lib/reportError";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError(error, { boundary: "global-error" });
  }, [error]);

  return (
    <html lang="en">
      <body className="flex items-center justify-center m-0 min-h-[100vh] p-4 bg-[#0a0a0a] text-[#e5e5e5] font-[-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_Helvetica,_Arial,_sans-serif]">
        <div className="text-center max-w-[24rem] w-[100%] rounded-16 p-8 bg-[#141414] border border-solid border-[#2a2a2a]">
          <h1
            className="font-semibold text-5"
            style={{
              margin: "0 0 0.5rem",
            }}
          >
            Something went wrong
          </h1>
          <p
            className="text-3.5 text-[#888]"
            style={{
              margin: "0 0 1.5rem",
            }}
          >
            An unexpected error occurred. You can try again.
          </p>
          <button
            onClick={() => reset()}
            className="font-medium cursor-pointer rounded-12 text-3.5 bg-palette-indigo text-[white]"
            style={{
              padding: "0.625rem 1.25rem",
              border: "none",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
